// ./src/components/features/Rentals/RentalAssetsList/RentalAssetsList.tsx
//
// Rental-assets catalog list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// Mirrors VouchersList (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import { RentalAsset, RENTAL_PRICING_MODEL_LABELS, venueRefName } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalRentalAssetsContext } from "@/contexts";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, countCardActions, type CardSkeletonShape } from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG, formatCurrency } from "@/utils";

export interface RentalAssetsListProps {
  data?: RentalAsset[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditAsset?: (asset: RentalAsset) => void;
  onDeleteAsset?: (asset: RentalAsset) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddAsset?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search / filter is narrowing the list. An empty result then means "no
   * match" — NOT "nothing here yet", so don't offer the first-run Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

const isDisabled = (asset: RentalAsset): boolean => asset.status === STATUSES.DISABLED;

const assetCurrency = (asset: RentalAsset): string => asset.currencyCode || "USD";

const StatusBadge: React.FC<{ asset: RentalAsset }> = ({ asset }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(asset)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const PriceSummary: React.FC<{ asset: RentalAsset }> = ({ asset }) => {
  const { t } = useTranslation(["rentals"]);
  const model = asset.defaultPricingModel || "";
  const modelLabel = RENTAL_PRICING_MODEL_LABELS[model] || model;

  if (asset.defaultUnitAmount == null) {
    return (
      <Text size="sm" c="dimmed">
        {t("rentals:assets.list.noPrice", { defaultValue: "No default price" })}
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <Text size="md" fw={600}>
        {formatCurrency(asset.defaultUnitAmount, assetCurrency(asset))}
      </Text>
      {modelLabel ? (
        <Badge size="xs" variant="light" color="grape">
          {modelLabel}
        </Badge>
      ) : null}
    </Group>
  );
};

const VendorCell: React.FC<{ asset: RentalAsset }> = ({ asset }) => {
  const name = venueRefName(asset.vendor, asset.vendorLabel);

  if (!name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <AppIcon iconName="Briefcase" size={15} />
      <Text size="md" lineClamp={1}>
        {name}
      </Text>
    </Group>
  );
};

const AssetIcon: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <ThemeIcon variant="light" color="grape" radius="xl" size={size}>
    <AppIcon iconName="Package" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  asset: RentalAsset;
  onEdit?: (a: RentalAsset) => void;
  onDelete?: (a: RentalAsset) => void;
  isDeleting: boolean;
}> = ({ asset, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(asset, "edit");
  const showDelete = !!onDelete && recordHasRight(asset, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(asset)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(asset)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  asset: RentalAsset;
  name: string;
  disabled: boolean;
  onEdit?: (a: RentalAsset) => void;
  onDelete?: (a: RentalAsset) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const AssetTableRow: React.FC<RowProps> = ({
  asset,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => asset.id && onToggleSelect?.(asset.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <AssetIcon />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge asset={asset} />
            </Group>
            {asset.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {asset.description}
              </Text>
            )}
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 220 }}>
        <VendorCell asset={asset} />
      </Table.Td>
      <Table.Td style={{ width: 220 }}>
        <PriceSummary asset={asset} />
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions asset={asset} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const AssetTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 220 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 220 }}>
      <Skeleton height={18} width={120} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const AssetCardComponent: React.FC<CardProps> = ({
  asset,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => asset.id && onToggleSelect?.(asset.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(asset, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(asset),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(asset, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(asset),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, asset, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <AssetIcon size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge asset={asset} />
          </Group>
          <Box mt={4}>
            <VendorCell asset={asset} />
          </Box>
          <Box mt={6}>
            <PriceSummary asset={asset} />
          </Box>
        </Box>
        {showActions && <RowActions asset={asset} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const AssetCard = React.memo(AssetCardComponent);

const AssetCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} gap={5} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const RentalAssetsListComponent: React.FC<RentalAssetsListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditAsset,
  onDeleteAsset,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddAsset,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["rentals", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditAsset, onDeleteAsset);

  const assetsContext = useOptionalRentalAssetsContext();

  const assets = dataProp ?? assetsContext?.rentalAssets ?? [];
  const loading = loadingProp ?? assetsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? assetsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? assetsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? assetsContext?.loadMore;
  const isRemoving = isRemovingProp ?? assetsContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (asset: RentalAsset) => {
      closeAllCards();
      onEditAsset?.(asset);
    },
    [onEditAsset, closeAllCards]
  );

  const handleDelete = useCallback(
    (asset: RentalAsset) => {
      closeAllCards();
      onDeleteAsset?.(asset);
    },
    [onDeleteAsset, closeAllCards]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = assetsContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && assets.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("rentals:assets.emptyState.noMatch", { defaultValue: "No rental assets match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("rentals:assets.emptyState.title", { defaultValue: "No rental assets yet" })}
          description={t("rentals:assets.emptyState.description", {
            defaultValue: "Build a catalog of rentable things (vehicles, equipment, furniture) to reuse across your events.",
          })}
          iconName="Package"
          actionLabel={t("rentals:assets.emptyState.actionLabel", { defaultValue: "Add rental asset" })}
          onAction={onAddAsset}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (asset: RentalAsset): boolean =>
    isSelectable &&
    ((!!onEditAsset && recordHasRight(asset, "edit")) || (!!onDeleteAsset && recordHasRight(asset, "remove")));

  const rowProps = (asset: RentalAsset): RowProps => ({
    asset,
    name: asset.name || "—",
    disabled: isDisabled(asset),
    onEdit: onEditAsset ? handleEdit : undefined,
    onDelete: onDeleteAsset ? handleDelete : undefined,
    highlighted: asset.id === highlightedId,
    isDeleting: isRemoving(asset.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(asset),
    selected: selection?.isSelected(asset.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = assets
    .filter((a) => rowIsSelectable(a))
    .map((a) => a.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("rentals:assets.list.columns.name", { defaultValue: "Asset" })}</Table.Th>
                <Table.Th style={{ width: 220 }}>
                  {t("rentals:assets.list.columns.vendor", { defaultValue: "Vendor" })}
                </Table.Th>
                <Table.Th style={{ width: 220 }}>
                  {t("rentals:assets.list.columns.price", { defaultValue: "Default price" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <AssetTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : assets.map((asset) => <AssetTableRow key={asset.id} {...rowProps(asset)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <AssetCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {assets.map((asset) => (
          <AssetCard
            key={asset.id}
            {...rowProps(asset)}
            forceClose={shouldForceClose(asset.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(asset.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(asset.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const RentalAssetsList = React.memo(RentalAssetsListComponent);
